import { useMutation } from '@apollo/client'
import { Add, Close } from '@mui/icons-material'
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem as MUIMenuItem,
  Select,
  Stack,
} from '@mui/material'
import dayjs from 'dayjs'
import advancedFormat from 'dayjs/plugin/advancedFormat'
import utc from 'dayjs/plugin/utc'
import { useEffect, useState } from 'react'

import { StoreHeaderWithQuery } from '@/components/stores/StoreHeaderWithQuery'
import {
  ActionRepeatSchedule,
  CREATE_ACTION,
  CreateActionMutation,
  CreateActionMutationVariables,
  DELETE_ACTION_GENERATOR,
  DeleteActionGeneratorMutation,
  DeleteActionGeneratorMutationVariables,
  GetActionGeneratorsQuery,
} from '@/graphql'
import useT from '@/i18n/useT'

import { MUITimePicker } from '../components/MUITimePicker'

dayjs.extend(advancedFormat)
dayjs.extend(utc)

export type EditRestockTimesActionGenerator =
  GetActionGeneratorsQuery['tenantActionGenerators']['results'][0] & {
    deleted?: boolean
    new?: boolean
    updated?: boolean
  }

type Props = {
  open: boolean
  onClose: () => void
  onSuccess?: () => void
  storeId?: string | null
  initialGenerators: EditRestockTimesActionGenerator[]
}

export const EditRestockTimes: React.FC<Props> = props => {
  const { open, onClose, initialGenerators, storeId, onSuccess } = props
  const { t } = useT()

  const [saving, setSaving] = useState(false)
  const [generators, setEditRestockTimesActionGenerators] =
    useState(initialGenerators)

  const repeatSchedule =
    initialGenerators[0]?.repeatSchedule || ActionRepeatSchedule.DAILY
  const initialStartDate = initialGenerators[0]?.startDate
    ? dayjs(initialGenerators[0].startDate.substring(0, 10))
    : dayjs()

  const [selectedDay, setSelectedDay] = useState<dayjs.Dayjs>(initialStartDate)

  useEffect(() => {
    if (open) {
      setEditRestockTimesActionGenerators(initialGenerators)
      setSelectedDay(
        initialGenerators[0]?.startDate
          ? dayjs(initialGenerators[0].startDate.substring(0, 10))
          : dayjs(),
      )
    }
  }, [open, initialGenerators])

  const updateGenAtIndex = (
    index: number,
    genUpdates: Partial<EditRestockTimesActionGenerator>,
  ) => {
    const updated = generators.map((gen, i) => {
      if (i === index) {
        return { ...gen, ...genUpdates }
      }
      return gen
    })
    setEditRestockTimesActionGenerators(updated)
  }

  const markAllUpdated = () => {
    setEditRestockTimesActionGenerators(
      generators.map(g => (g.new || g.deleted ? g : { ...g, updated: true })),
    )
  }

  const appendNewTime = () => {
    const newOne: EditRestockTimesActionGenerator = {
      ...generators[0],
      dueDateTime: null,
      id: '',
      new: true,
      deleted: false,
    }
    setEditRestockTimesActionGenerators([...generators, newOne])
  }

  // MUTATION STUFF BELOW HERE
  const [deleteActionGenerator] = useMutation<
    DeleteActionGeneratorMutation,
    DeleteActionGeneratorMutationVariables
  >(DELETE_ACTION_GENERATOR)
  const [createAction] = useMutation<
    CreateActionMutation,
    CreateActionMutationVariables
  >(CREATE_ACTION)

  const handleSave = async () => {
    setSaving(true)
    if (!storeId) {
      console.error('Store ID is required to save the action generators')
      setSaving(false)
      return
    }
    // instead of an update mutation, we are going to delete and create a new ones
    // we can look at making an update mutation if needed
    const generatorsToDelete = generators.filter(
      gen => gen.deleted || (gen.updated && !gen.new),
    )
    const generatorsToCreate = generators.filter(gen => gen.new || gen.updated)

    const deletePromises = generatorsToDelete.map(gen => {
      return deleteActionGenerator({
        variables: {
          input: {
            id: gen.id,
          },
        },
      })
    })
    await Promise.all(deletePromises)
    const createPromises = generatorsToCreate.map(gen => {
      return createAction({
        variables: {
          input: {
            title: gen.title,
            masterCategoryId: gen.masterCategoryId,
            isRestock: true,
            actionType: gen.actionType,
            repeatSchedule: gen.repeatSchedule,
            dueDate:
              repeatSchedule === ActionRepeatSchedule.DAILY
                ? dayjs().format('YYYY-MM-DD')
                : selectedDay.format('YYYY-MM-DD'),
            dueDateTime: dayjs(gen.dueDateTime).format('HH:mm:ss'),
            storeIds: [storeId],
            createOnePerStore: true,
          },
        },
      })
    })
    await Promise.all(createPromises)
    onSuccess?.()
    setSaving(false)
  }

  return generators.length > 0 ? (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>{`${t('common.edit')} ${generators[0].title} ${t('common.restockTimes')}`}</DialogTitle>
      <DialogContent>
        {storeId && <StoreHeaderWithQuery id={storeId} />}

        {repeatSchedule === ActionRepeatSchedule.WEEKLY && (
          <FormControl fullWidth sx={{ mt: 4 }}>
            <InputLabel>{t('common.dayOfWeek')}</InputLabel>
            <Select
              value={selectedDay.day()}
              label={t('common.dayOfWeek')}
              onChange={e => {
                const newDay = e.target.value as number
                setSelectedDay(selectedDay.day(newDay))
                markAllUpdated()
              }}
            >
              {[0, 1, 2, 3, 4, 5, 6].map(d => (
                <MUIMenuItem key={d} value={d}>
                  {dayjs().day(d).format('dddd')}
                </MUIMenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {repeatSchedule === ActionRepeatSchedule.MONTHLY && (
          <FormControl fullWidth sx={{ mt: 4 }}>
            <InputLabel>{t('common.dayOfMonth')}</InputLabel>
            <Select
              value={selectedDay.date()}
              label={t('common.dayOfMonth')}
              onChange={e => {
                const newDate = e.target.value as number
                setSelectedDay(selectedDay.date(newDate))
                markAllUpdated()
              }}
            >
              {Array.from({ length: 31 }, (_, i) => i + 1).map(d => (
                <MUIMenuItem key={d} value={d}>
                  {dayjs().date(d).format('Do')}
                </MUIMenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        <Stack
          spacing={4}
          mb={2}
          mt={repeatSchedule === ActionRepeatSchedule.DAILY ? 8 : 4}
        >
          {generators.map((gen, index) => {
            return !gen.deleted ? (
              <Stack
                key={index}
                direction={'row'}
                spacing={2}
                alignItems={'center'}
              >
                <MUITimePicker
                  label={t('createRestockAction.dueTime')}
                  dateTime={gen.dueDateTime ? dayjs(gen.dueDateTime) : null}
                  onDateTimeChange={newDate => {
                    updateGenAtIndex(index, {
                      dueDateTime: newDate?.toISOString() || '',
                      updated: true,
                    })
                  }}
                />
                <IconButton
                  onClick={() => {
                    updateGenAtIndex(index, { deleted: true })
                  }}
                >
                  <Close />
                </IconButton>
              </Stack>
            ) : null
          })}
        </Stack>
        <Button startIcon={<Add />} onClick={appendNewTime}>
          {t('common.addMore')}
        </Button>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={onClose}>
          {t('common.cancel')}
        </Button>
        <Button variant="contained" onClick={handleSave} loading={saving}>
          {t('common.save')}
        </Button>
      </DialogActions>
    </Dialog>
  ) : null
}
